import React, { useState, useRef, useEffect } from 'react';
import './QuestionCard.css';

/**
 * QuestionCard — reusable "model asks the user" mechanism.
 *
 * The assistant ends an answer with an ```ask_user fence:
 *   { "question": "What date range would you like?",
 *     "options": [
 *       { "label": "Last 30 days", "value": "Use the last 30 days" },
 *       { "label": "Month to date", "value": "Use month to date" },
 *       { "label": "Type your own", "input": true, "placeholder": "e.g. May 1 – June 15" },
 *       { "label": "Never mind" }       // no value, no input → dismisses
 *     ] }
 *
 * - value option  → clicking sends that text as the user's next chat message.
 * - input option  → clicking reveals an inline text field; submitting sends
 *                   the typed text (optionally `prefix` + text, e.g.
 *                   prefix: "Use this date range: ").
 * - bare option   → resolves the card without sending anything.
 *
 * One card per answer; built generically so any feature (scheduled tasks,
 * clarifying ambiguous questions, forecast actions, ...) can pose choices
 * without bespoke UI.
 */
const QuestionCard = ({ data, onAnswer, disabled = false }) => {
  const [resolved, setResolved] = useState(null);     // label of the chosen option
  const [inputOpen, setInputOpen] = useState(null);   // the active input option
  const [inputText, setInputText] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputOpen && inputRef.current) inputRef.current.focus();
  }, [inputOpen]);

  if (!data || !data.question || !Array.isArray(data.options) || data.options.length === 0) {
    return null;
  }

  const send = (text) => {
    if (typeof onAnswer === 'function' && text) onAnswer(text);
  };

  const handleClick = (option) => {
    if (resolved || disabled) return;
    if (option.input) {
      setInputOpen(option);
      return;
    }
    setResolved(option.label || option.value || 'OK');
    setInputOpen(null);
    if (option.value && !option.dismiss) send(option.value);
  };

  const handleSubmitInput = () => {
    const text = inputText.trim();
    if (!text || resolved || disabled) return;
    setResolved(text);
    send(`${inputOpen.prefix || ''}${text}`);
    setInputOpen(null);
  };

  return (
    <div className={`ai-question-card${resolved ? ' ai-question-card--resolved' : ''}`}>
      <div className="ai-question-card__question">{data.question}</div>
      <div className="ai-question-card__options">
        {data.options.slice(0, 5).map((option, i) => {
          const isChosen = resolved && resolved === (option.label || option.value);
          const isPrimary = option.primary !== false && i === 0 && !option.input;
          const isOpenInput = inputOpen === option;
          return (
            <button
              key={i}
              type="button"
              className={[
                'ai-question-card__option',
                isPrimary ? 'ai-question-card__option--primary' : '',
                option.input ? 'ai-question-card__option--input' : '',
                isChosen || isOpenInput ? 'ai-question-card__option--chosen' : ''
              ].filter(Boolean).join(' ')}
              onClick={() => handleClick(option)}
              disabled={!!resolved || disabled}
            >
              {option.label || option.value}
            </button>
          );
        })}
      </div>
      {inputOpen && !resolved && (
        <div className="ai-question-card__input-row">
          <input
            ref={inputRef}
            type="text"
            className="ai-question-card__input"
            placeholder={inputOpen.placeholder || 'Type your answer…'}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSubmitInput(); }}
            disabled={disabled}
          />
          <button
            type="button"
            className="ai-question-card__option ai-question-card__option--primary"
            onClick={handleSubmitInput}
            disabled={!inputText.trim() || disabled}
          >
            Send
          </button>
        </div>
      )}
      {resolved && (
        <div className="ai-question-card__resolved-note">✓ {resolved}</div>
      )}
    </div>
  );
};

export default QuestionCard;
